import { supabase } from './supabase';
import { Extension, ExtensionSettings } from '../types';

export interface ExtensionCatalogItem {
  id?: string;
  extension_id: string;
  name: string;
  icon: string;
  description: string;
  enabled: boolean;
  coming_soon: boolean;
  featured: boolean;
  is_paid?: boolean;
  version?: string;
  category?: string;
  tags?: string[];
  settings_schema?: Record<string, any>;
  default_settings?: Record<string, any>;
  min_app_version?: string;
  max_app_version?: string;
  required_permissions?: string[];
  display_order?: number;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

/**
 * Get all extensions from the catalog
 */
export async function getExtensionsCatalog(): Promise<ExtensionCatalogItem[]> {
  const { data, error } = await supabase
    .from('extensions_catalog')
    .select('*')
    .order('display_order', { ascending: true })
    .order('name', { ascending: true });

  if (error) {
    console.error('Error fetching extensions catalog:', error);
    throw new Error('Failed to load extensions catalog');
  }

  return data || [];
}

/**
 * Get a specific extension from the catalog
 */
export async function getExtensionFromCatalog(extensionId: string): Promise<ExtensionCatalogItem | null> {
  const { data, error } = await supabase
    .from('extensions_catalog')
    .select('*')
    .eq('extension_id', extensionId)
    .single();

  if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
    console.error('Error fetching extension from catalog:', error);
    throw new Error('Failed to load extension');
  }

  return data || null;
}

/**
 * Get extensions by category
 */
export async function getExtensionsByCategory(category: string): Promise<ExtensionCatalogItem[]> {
  const { data, error } = await supabase
    .from('extensions_catalog')
    .select('*')
    .eq('category', category)
    .eq('enabled', true)
    .order('display_order', { ascending: true });

  if (error) {
    console.error('Error fetching extensions by category:', error);
    throw new Error('Failed to load extensions');
  }

  return data || [];
}

/**
 * Get featured extensions
 */
export async function getFeaturedExtensions(): Promise<ExtensionCatalogItem[]> {
  const { data, error } = await supabase
    .from('extensions_catalog')
    .select('*')
    .eq('featured', true)
    .eq('enabled', true)
    .order('display_order', { ascending: true });

  if (error) {
    console.error('Error fetching featured extensions:', error);
    throw new Error('Failed to load featured extensions');
  }

  return data || [];
}

/**
 * Search extensions by name or description
 */
export async function searchExtensions(query: string): Promise<ExtensionCatalogItem[]> {
  const { data, error } = await supabase
    .from('extensions_catalog')
    .select('*')
    .or(`name.ilike.%${query}%,description.ilike.%${query}%`)
    .eq('enabled', true)
    .order('display_order', { ascending: true });

  if (error) {
    console.error('Error searching extensions:', error);
    throw new Error('Failed to search extensions');
  }

  return data || [];
}

/**
 * Convert catalog items to the Extension interface used by the UI
 */
export function catalogItemToExtension(catalogItem: ExtensionCatalogItem, isActivated: boolean = false): Extension {
  return {
    id: catalogItem.extension_id,
    name: catalogItem.name,
    icon: catalogItem.icon,
    description: catalogItem.description,
    activated: isActivated,
    // Show as coming soon if either disabled or explicitly marked as coming soon
    comingSoon: !catalogItem.enabled || catalogItem.coming_soon,
    is_paid: catalogItem.is_paid || false,
  };
}

/**
 * Convert catalog items to extension settings format
 */
export function catalogItemToExtensionSettings(catalogItem: ExtensionCatalogItem): ExtensionSettings {
  if (!catalogItem.settings_schema) {
    return {};
  }

  return {
    [catalogItem.extension_id]: {
      name: `${catalogItem.name} settings`,
      ...catalogItem.settings_schema,
    },
  };
}

/**
 * Get all enabled extensions as Extension objects with activation status
 */
export async function getExtensionsWithActivationStatus(): Promise<{ extensions: Extension[]; extensionSettings: ExtensionSettings }> {
  try {
    // Get all extensions from catalog (including disabled ones)
    const catalogItems = await getExtensionsCatalog();

    // Get user's activated extensions (only if user is signed in)
    let activatedExtensionIds: string[] = [];
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Get extension settings using RPC
        const { data: extensionSettings } = await supabase.rpc('get_my_extension_settings');
        
        if (extensionSettings) {
          // Get all extension IDs where enabled is true
          activatedExtensionIds = Object.entries(extensionSettings)
            .filter(([_, settings]: [string, any]) => settings.enabled)
            .map(([extensionId, _]) => extensionId);
        }
      }
    } catch (error) {
      // User not signed in or error getting settings, continue with empty activations
      console.log('User not signed in or no settings, showing extensions without activation status');
    }

    // Convert to Extension objects
    // Show all extensions, but mark disabled ones as coming_soon
    const extensions = catalogItems.map(item => 
      catalogItemToExtension(item, activatedExtensionIds.includes(item.extension_id))
    );

    // Build extension settings object
    const extensionSettings: ExtensionSettings = {};
    catalogItems.forEach(item => {
      if (item.settings_schema) {
        extensionSettings[item.extension_id] = {
          name: `${item.name} settings`,
          ...item.settings_schema,
        };
      }
    });

    return { extensions, extensionSettings };
  } catch (error) {
    console.error('Error getting extensions with activation status:', error);
    throw new Error('Failed to load extensions');
  }
}

/**
 * Get all available categories
 */
export async function getExtensionCategories(): Promise<string[]> {
  const { data, error } = await supabase
    .from('extensions_catalog')
    .select('category')
    .not('category', 'is', null)
    .eq('enabled', true);

  if (error) {
    console.error('Error fetching extension categories:', error);
    throw new Error('Failed to load categories');
  }

  // Get unique categories
  const uniqueCategories = new Set(data?.map(item => item.category).filter(Boolean));
  const categories = Array.from(uniqueCategories) as string[];
  return categories.sort();
}

/**
 * Admin function to create/update an extension in the catalog
 * Note: This requires admin permissions
 */
export async function upsertExtensionCatalogItem(extension: Omit<ExtensionCatalogItem, 'id' | 'created_at' | 'updated_at'>): Promise<ExtensionCatalogItem> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('You must be signed in to manage extensions');
  }

  const extensionData = {
    ...extension,
    updated_by: user.id,
    created_by: user.id, // Will be ignored on update
  };

  const { data, error } = await supabase
    .from('extensions_catalog')
    .upsert(extensionData, { 
      onConflict: 'extension_id',
      ignoreDuplicates: false 
    })
    .select()
    .single();

  if (error) {
    console.error('Error upserting extension catalog item:', error);
    throw new Error('Failed to save extension');
  }

  return data;
}